import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useWebinarT } from '@/hooks/useWebinarT';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  fetchEventHistory,
  fetchMyPublicQuizSubmission,
  fetchPublicOffer,
  fetchPublicPlayback,
  fetchPublicQuiz,
  fetchPublicSession,
  fetchPublicSurvey,
  postAnalyticsEvent,
} from '@/features/live/liveThunks';
import { fetchMyPublicSurveySubmission } from '@/features/webinar/webinarThunks';
import type { RootState } from '@/app/store';
import SplashScreen from '@/components/common/SplashScreen';
import FootBarReplay from '@/components/pages/replay/FootBarReplay';
import YouTubeReplayPlayer from '@/components/pages/replay/YouTubeReplayPlayer';
import ChatViewReplay from '@/components/pages/replay/ChatViewReplay';
import Controle from '@/components/pages/replay/Controle';
import SurveyCard from '@/components/common/asset/SurveyCard';
import QuizCard from '@/components/common/asset/QuizCard';
import OfferCard from '@/components/common/asset/OfferCard';
import PopupBuyerCard from '@/components/common/asset/PopupBuyerCard';
import SharedResourceViewer from '@/components/pages/live/attendee/SharedResourceViewer';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { cn } from '@/lib/utils';
import type { WebinarEventHistoryDto } from '@/features/live/types';
import { getActiveSharedResource } from '@/lib/replaySharedResources';

const YOUTUBE_REPLAY_BROADCAST_DELAY_S = 5;

interface LiveReaction {
  id: string;
  text: string;
  left: number;
  offsetSeconds: number;
}

interface ActiveSurvey {
  resourceId: number;
  isVisible: boolean;
  showResults: boolean;
}

interface ActiveQuiz {
  resourceId: number;
  isVisible: boolean;
}

interface ActiveOffer {
  resourceId: number;
  isVisible: boolean;
}

const getVisibleLiveReactions = (
  eventHistory: WebinarEventHistoryDto | null,
  currentTime: number,
): LiveReaction[] => {
  if (!eventHistory?.events) {
    return [];
  }

  return eventHistory.events
    .filter(
      (event) =>
        event.eventType === 'LIVE_REACTION' &&
        event.offsetSeconds <= currentTime &&
        event.payload?.reaction,
    )
    .map((event, index) => {
      const seed = event.offsetSeconds * 1000 + index;
      const randomValue = Math.sin(seed) * 10000;
      const normalized = randomValue - Math.floor(randomValue);
      const baseLeft = 5 + normalized * 30;
      const offset = (Math.sin(seed * 2) * 10000 - Math.floor(Math.sin(seed * 2) * 10000)) * 5;
      const left = baseLeft + offset;

      return {
        id: `reaction-${event.offsetSeconds}-${index}`,
        text: event.payload.reaction as string,
        left,
        offsetSeconds: event.offsetSeconds,
      };
    });
};

const getActiveSurvey = (
  eventHistory: WebinarEventHistoryDto | null,
  currentTime: number,
): ActiveSurvey | null => {
  if (!eventHistory?.events) {
    return null;
  }

  const surveyEvents = eventHistory.events.filter(
    (event) =>
      event.payload?.resourceType === 'SURVEY_TEMPLATE' &&
      (event.eventType === 'RESOURCE_SHOW' || event.eventType === 'RESOURCE_HIDE'),
  );

  if (surveyEvents.length === 0) {
    return null;
  }

  const sortedEvents = surveyEvents.sort((a, b) => {
    if (a.offsetSeconds !== b.offsetSeconds) {
      return a.offsetSeconds - b.offsetSeconds;
    }
    const aResourceId = (a.payload?.resourceId as number | undefined) || 0;
    const bResourceId = (b.payload?.resourceId as number | undefined) || 0;
    return aResourceId - bResourceId;
  });

  let activeSurvey: ActiveSurvey | null = null;

  for (let i = sortedEvents.length - 1; i >= 0; i--) {
    const event = sortedEvents[i];
    if (event.offsetSeconds > currentTime) {
      continue;
    }

    const resourceId = event.payload?.resourceId as number | undefined;
    if (!resourceId) continue;

    if (event.eventType === 'RESOURCE_SHOW') {
      const showTime = event.offsetSeconds;
      const hideEvent = sortedEvents.find(
        (e) =>
          e.offsetSeconds > showTime &&
          e.offsetSeconds <= currentTime &&
          e.eventType === 'RESOURCE_HIDE' &&
          (e.payload?.resourceId as number | undefined) === resourceId,
      );

      if (!hideEvent) {
        activeSurvey = {
          resourceId,
          isVisible: true,
          showResults: false,
        };
        break;
      }
    }
  }

  return activeSurvey;
};

const getActiveQuiz = (
  eventHistory: WebinarEventHistoryDto | null,
  currentTime: number,
): ActiveQuiz | null => {
  if (!eventHistory?.events) {
    return null;
  }

  const quizEvents = eventHistory.events.filter(
    (event) =>
      event.payload?.resourceType === 'QUIZ_TEMPLATE' &&
      (event.eventType === 'RESOURCE_SHOW' || event.eventType === 'RESOURCE_HIDE'),
  );

  if (quizEvents.length === 0) {
    return null;
  }

  const sortedEvents = quizEvents.sort((a, b) => {
    if (a.offsetSeconds !== b.offsetSeconds) {
      return a.offsetSeconds - b.offsetSeconds;
    }
    const aResourceId = (a.payload?.resourceId as number | undefined) || 0;
    const bResourceId = (b.payload?.resourceId as number | undefined) || 0;
    return aResourceId - bResourceId;
  });

  let activeQuiz: ActiveQuiz | null = null;

  for (let i = sortedEvents.length - 1; i >= 0; i--) {
    const event = sortedEvents[i];
    if (event.offsetSeconds > currentTime) {
      continue;
    }

    const resourceId = event.payload?.resourceId as number | undefined;
    if (!resourceId) continue;

    if (event.eventType === 'RESOURCE_SHOW') {
      const showTime = event.offsetSeconds;
      const hideEvent = sortedEvents.find(
        (e) =>
          e.offsetSeconds > showTime &&
          e.offsetSeconds <= currentTime &&
          e.eventType === 'RESOURCE_HIDE' &&
          (e.payload?.resourceId as number | undefined) === resourceId,
      );

      if (!hideEvent) {
        activeQuiz = {
          resourceId,
          isVisible: true,
        };
        break;
      }
    }
  }

  return activeQuiz;
};

const getActiveOffer = (
  eventHistory: WebinarEventHistoryDto | null,
  currentTime: number,
): ActiveOffer | null => {
  if (!eventHistory?.events) {
    return null;
  }

  const offerEvents = eventHistory.events.filter(
    (event) =>
      event.payload?.resourceType === 'OFFER_TEMPLATE' &&
      (event.eventType === 'RESOURCE_SHOW' || event.eventType === 'RESOURCE_HIDE'),
  );

  if (offerEvents.length === 0) {
    return null;
  }

  const sortedEvents = offerEvents.sort((a, b) => {
    if (a.offsetSeconds !== b.offsetSeconds) {
      return a.offsetSeconds - b.offsetSeconds;
    }
    const aResourceId = (a.payload?.resourceId as number | undefined) || 0;
    const bResourceId = (b.payload?.resourceId as number | undefined) || 0;
    return aResourceId - bResourceId;
  });

  let activeOffer: ActiveOffer | null = null;

  for (let i = sortedEvents.length - 1; i >= 0; i--) {
    const event = sortedEvents[i];
    if (event.offsetSeconds > currentTime) {
      continue;
    }

    const resourceId = event.payload?.resourceId as number | undefined;
    if (!resourceId) continue;

    if (event.eventType === 'RESOURCE_SHOW') {
      const showTime = event.offsetSeconds;
      const hideEvent = sortedEvents.find(
        (e) =>
          e.offsetSeconds > showTime &&
          e.offsetSeconds <= currentTime &&
          e.eventType === 'RESOURCE_HIDE' &&
          (e.payload?.resourceId as number | undefined) === resourceId,
      );

      if (!hideEvent) {
        activeOffer = {
          resourceId,
          isVisible: true,
        };
        break;
      }
    }
  }

  return activeOffer;
};

const getVisiblePurchaseNotifications = (
  eventHistory: WebinarEventHistoryDto | null,
  currentTime: number,
): Array<{
  offsetSeconds: number;
  message: string;
  icon: string;
  showIcon: boolean;
  offerColors: {
    backgroundColor: string;
    borderColor: string;
    textColor: string;
    iconColor: string;
    iconBackgroundColor: string;
  };
}> => {
  if (!eventHistory?.events) {
    return [];
  }

  return eventHistory.events
    .filter((event) => {
      if (event.eventType !== 'PURCHASE_NOTIFICATION' || event.offsetSeconds > currentTime) {
        return false;
      }

      const payload = event.payload as Record<string, unknown>;
      const publicPayload = payload?.public as Record<string, unknown> | undefined;
      return publicPayload && typeof publicPayload.message === 'string';
    })
    .map((event) => {
      const payload = event.payload as Record<string, unknown>;
      // Déjà entièrement résolue côté back au moment de l'achat (offre puis repli legacy, cf.
      // IntegrationWebinarServiceImpl.findNotificationConfiguration) et recopiée dans le payload
      // stocké : pas besoin de refetch/résoudre quoi que ce soit côté front pour le replay.
      const publicPayload = payload?.public as Record<string, unknown>;
      return {
        offsetSeconds: event.offsetSeconds,
        message: (publicPayload?.message as string) || '',
        icon: (publicPayload?.icon as string) || 'award',
        showIcon: (publicPayload?.displayIcon as boolean) ?? true,
        offerColors: {
          backgroundColor: (publicPayload?.backgroundColor as string) || '#FFFFFF',
          borderColor: (publicPayload?.borderColor as string) || 'transparent',
          textColor: (publicPayload?.textColor as string) || '#000000',
          iconColor: (publicPayload?.iconColor as string) || '#000000',
          iconBackgroundColor: (publicPayload?.backgroundIcon as string) || '#000000',
        },
      };
    });
};

export default function ReplayLivePage() {
  const { t } = useWebinarT('webinars');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [purchaseNotificationQueue, setPurchaseNotificationQueue] = useState<
    Array<{
      message: string;
      icon: string;
      showIcon: boolean;
      offerColors?: {
        backgroundColor: string;
        borderColor: string;
        textColor: string;
        iconColor: string;
        iconBackgroundColor: string;
      };
    }>
  >([]);
  const [currentPurchaseNotification, setCurrentPurchaseNotification] = useState<{
    message: string;
    icon: string;
    showIcon: boolean;
    offerColors?: {
      backgroundColor: string;
      borderColor: string;
      textColor: string;
      iconColor: string;
      iconBackgroundColor: string;
    };
  } | null>(null);
  const purchaseNotificationTimeoutRef = useRef<number | null>(null);
  const processedPurchaseNotificationsRef = useRef<Set<number>>(new Set());
  const [isScrubbingProgress, setIsScrubbingProgress] = useState(false);
  const { webinarId: webinarIdParam, sessionIdentifier } = useParams<{
    webinarId: string;
    sessionIdentifier: string;
  }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const webinarId = webinarIdParam ? parseInt(webinarIdParam, 10) : null;
  const playbackInfo = useAppSelector((s: RootState) => s.live.playbackInfo);
  const playbackLoading = useAppSelector((s: RootState) => s.live.playbackLoading);
  const playbackError = useAppSelector((s: RootState) => s.live.playbackError);
  const visualIdentity = useAppSelector((s: RootState) => s.live.publicSession?.visualIdentity);
  const eventHistory = useAppSelector((s: RootState) => s.live.eventHistory);
  const publicSession = useAppSelector((s: RootState) => s.live.publicSession);
  const publicSurvey = useAppSelector((s: RootState) => s.live.publicSurvey);
  const publicQuiz = useAppSelector((s: RootState) => s.live.publicQuiz);
  const publicOffer = useAppSelector((s: RootState) => s.live.publicOffer);
  const currentPublicSurveySubmission = useAppSelector(
    (s: RootState) => s.webinar.currentPublicSurveySubmission,
  );
  const publicQuizSubmission = useAppSelector((s: RootState) => s.live.publicQuizSubmission);
  const hasUserSubmittedSurvey =
    currentPublicSurveySubmission?.templateId === publicSurvey?.id &&
    (currentPublicSurveySubmission?.answers?.length ?? 0) > 0;

  const hasUserSubmittedQuiz =
    publicQuizSubmission?.templateId === publicQuiz?.id &&
    (publicQuizSubmission?.answers?.length ?? 0) > 0;

  const session = publicSession?.session;
  const isYouTube = session?.transmissionMode === 'YOUTUBE';

  // La VOD YouTube démarre à actualStartTime, alors que les offsets des events sont calés sur startedAt
  // (clic « démarrer la session »). On décale donc le temps de lecture de ce delta pour aligner les interactions.
  const eventOffsetDeltaSeconds = useMemo(() => {
    if (!isYouTube || !session?.startedAt || !session?.youtubeActualStartTime) return 0;
    const started = DateTime.fromISO(session.startedAt, { zone: 'utc' }).toMillis();
    const actual = DateTime.fromISO(session.youtubeActualStartTime, { zone: 'utc' }).toMillis();
    if (Number.isNaN(started) || Number.isNaN(actual)) return 0;
    return Math.max(0, (started - actual) / 1000);
  }, [isYouTube, session?.startedAt, session?.youtubeActualStartTime]);

  const eventMatchTime = Math.max(
    0,
    currentTime - eventOffsetDeltaSeconds - (isYouTube ? YOUTUBE_REPLAY_BROADCAST_DELAY_S : 0),
  );

  useEffect(() => {
    if (!webinarId || !sessionIdentifier || !token) {
      return;
    }

    dispatch(
      postAnalyticsEvent({
        webinarId,
        sessionIdentifier,
        authorization: token,
        input: {
          eventType: 'JOINED',
          payloadJson: JSON.stringify({ timeNow: new Date().getTime() }),
          occurredAt: new Date().toISOString(),
        },
      }),
    );
  }, [webinarId, sessionIdentifier, token, dispatch]);

  const sendReplayHeartbeat = useCallback(() => {
    if (!webinarId || !sessionIdentifier || !token) {
      return Promise.resolve();
    }
    return dispatch(
      postAnalyticsEvent({
        webinarId,
        sessionIdentifier,
        authorization: token,
        input: {
          eventType: 'REPLAY_HEARTBEAT',
          payloadJson: JSON.stringify({ timeNow: new Date().getTime() }),
          occurredAt: new Date().toISOString(),
        },
      }),
    );
  }, [webinarId, sessionIdentifier, token, dispatch]);

  useEffect(() => {
    if (!isPlaying) {
      return;
    }
    sendReplayHeartbeat();
    const intervalId = setInterval(() => {
      sendReplayHeartbeat();
    }, 60000);
    return () => {
      clearInterval(intervalId);
    };
  }, [isPlaying, sendReplayHeartbeat]);

  const activeSurvey = getActiveSurvey(eventHistory, eventMatchTime);
  const activeQuiz = getActiveQuiz(eventHistory, eventMatchTime);
  const activeOffer = getActiveOffer(eventHistory, eventMatchTime);
  const activeSharedResource = getActiveSharedResource(eventHistory, eventMatchTime);

  const applyPlaybackTime = (time: number, options?: { isSeek?: boolean }) => {
    const normalizedTime = Math.max(0, time);
    if (options?.isSeek) {
      processedPurchaseNotificationsRef.current.clear();
      setPurchaseNotificationQueue([]);
      setCurrentPurchaseNotification(null);
      if (purchaseNotificationTimeoutRef.current) {
        clearTimeout(purchaseNotificationTimeoutRef.current);
        purchaseNotificationTimeoutRef.current = null;
      }
    }
    setCurrentTime(normalizedTime);
  };

  const syncDurationFromVideo = (video: HTMLVideoElement) => {
    const videoDuration = video.duration;
    if (videoDuration && isFinite(videoDuration) && videoDuration > 0) {
      setDuration(videoDuration);
    }
  };

  const playVideo = () => {
    if (videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        setIsPlaying(true);
        playPromise.catch(() => {
          setIsPlaying(false);
        });
      }
    }
  };

  const pauseVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const goBack = () => {
    if (videoRef.current) {
      const newTime = Math.max(0, videoRef.current.currentTime - 10);
      videoRef.current.currentTime = newTime;
      applyPlaybackTime(newTime, { isSeek: true });
    }
  };

  const goForward = () => {
    if (videoRef.current) {
      const newTime = Math.min(
        videoRef.current.currentTime + 10,
        videoRef.current.duration || videoRef.current.currentTime + 10,
      );
      videoRef.current.currentTime = newTime;
      applyPlaybackTime(newTime, { isSeek: true });
    }
  };

  const refreshVideo = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      applyPlaybackTime(0, { isSeek: true });
      videoRef.current.load();
    }
  };

  const actualDuration = duration > 0 ? duration : videoRef.current?.duration || 0;
  const progressPercentage = actualDuration > 0 ? (currentTime / actualDuration) * 100 : 0;

  const visibleReactions = getVisibleLiveReactions(eventHistory, eventMatchTime);

  const seekToProgressRatio = (clientX: number, progressBar: HTMLDivElement) => {
    if (!videoRef.current) return;

    const videoDuration = videoRef.current.duration;
    const resolvedDuration = duration > 0 ? duration : videoDuration || 0;
    if (resolvedDuration <= 0) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const width = rect.width;
    if (width <= 0) return;

    const clickPercentage = Math.max(0, Math.min(1, clickX / width));
    const newTime = clickPercentage * resolvedDuration;
    videoRef.current.currentTime = newTime;
    applyPlaybackTime(newTime, { isSeek: true });
  };

  const handleProgressPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsScrubbingProgress(true);
    seekToProgressRatio(e.clientX, e.currentTarget);
  };

  const handleProgressPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isScrubbingProgress) return;
    seekToProgressRatio(e.clientX, e.currentTarget);
  };

  const handleProgressPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setIsScrubbingProgress(false);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    seekToProgressRatio(e.clientX, e.currentTarget);
  };

  useEffect(() => {
    if (webinarId && sessionIdentifier) {
      dispatch(
        fetchPublicSession({
          webinarId,
          sessionIdentifier,
        }),
      );
    }
  }, [dispatch, webinarId, sessionIdentifier]);

  useEffect(() => {
    if (webinarId && sessionIdentifier && token) {
      // Playback MP4 réservé au natif : on attend de connaître la session pour ne pas appeler en YouTube.
      if (session && !isYouTube) {
        dispatch(
          fetchPublicPlayback({
            webinarId,
            sessionIdentifier,
            authorization: token,
          }),
        );
      }
      dispatch(
        fetchEventHistory({
          webinarId,
          sessionIdentifier,
          authorization: token,
        }),
      );
    }
  }, [dispatch, webinarId, sessionIdentifier, token, session, isYouTube]);

  useEffect(() => {
    if (activeSurvey?.isVisible && webinarId && token) {
      const surveyResourceId = activeSurvey.resourceId;
      if (!publicSurvey || publicSurvey.id !== surveyResourceId) {
        dispatch(
          fetchPublicSurvey({
            webinarId,
            surveyId: surveyResourceId,
            authorization: token,
          }),
        );
      }
      if (currentPublicSurveySubmission?.templateId !== surveyResourceId) {
        dispatch(
          fetchMyPublicSurveySubmission({
            webinarId,
            templateId: surveyResourceId,
            authorization: token,
          }),
        );
      }
    }
  }, [
    activeSurvey?.resourceId,
    activeSurvey?.isVisible,
    activeSurvey?.showResults,
    webinarId,
    token,
    publicSurvey?.id,
    publicSession?.session?.id,
    currentPublicSurveySubmission?.templateId,
    dispatch,
  ]);

  useEffect(() => {
    if (activeQuiz?.isVisible && webinarId && token) {
      if (!publicQuiz || publicQuiz.id !== activeQuiz.resourceId) {
        dispatch(
          fetchPublicQuiz({
            webinarId,
            quizId: activeQuiz.resourceId,
            authorization: token,
          }),
        );
      }
      if (publicQuizSubmission?.templateId !== activeQuiz.resourceId) {
        dispatch(
          fetchMyPublicQuizSubmission({
            webinarId,
            templateId: activeQuiz.resourceId,
            authorization: token,
          }),
        );
      }
    }
  }, [
    activeQuiz?.resourceId,
    activeQuiz?.isVisible,
    webinarId,
    token,
    publicQuiz?.id,
    publicQuizSubmission?.templateId,
    dispatch,
  ]);

  useEffect(() => {
    if (activeOffer?.isVisible && webinarId && token && sessionIdentifier) {
      if (!publicOffer || publicOffer.id !== activeOffer.resourceId) {
        dispatch(
          fetchPublicOffer({
            webinarId,
            offerId: activeOffer.resourceId,
            authorization: token,
          }),
        );

        dispatch(
          postAnalyticsEvent({
            webinarId,
            sessionIdentifier,
            authorization: token,
            input: {
              eventType: 'OFFER_SEEN',
              payloadJson: JSON.stringify({
                offerId: activeOffer.resourceId,
              }),
              occurredAt: new Date().toISOString(),
            },
          }),
        );
      }
    }
  }, [activeOffer, webinarId, token, sessionIdentifier, publicOffer, dispatch]);

  const showNextPurchaseNotification = () => {
    if (purchaseNotificationTimeoutRef.current) {
      clearTimeout(purchaseNotificationTimeoutRef.current);
      purchaseNotificationTimeoutRef.current = null;
    }

    setPurchaseNotificationQueue((prevQueue) => {
      if (prevQueue.length === 0) {
        setCurrentPurchaseNotification(null);
        return prevQueue;
      }

      const [nextNotification, ...remainingQueue] = prevQueue;
      setCurrentPurchaseNotification(nextNotification);

      purchaseNotificationTimeoutRef.current = window.setTimeout(() => {
        showNextPurchaseNotification();
      }, 3000);

      return remainingQueue;
    });
  };

  const addPurchaseNotificationToQueue = (notification: {
    message: string;
    icon: string;
    showIcon: boolean;
    offerColors?: {
      backgroundColor: string;
      borderColor: string;
      textColor: string;
      iconColor: string;
      iconBackgroundColor: string;
    };
  }) => {
    setPurchaseNotificationQueue((prevQueue) => [...prevQueue, notification]);
  };

  const handleClosePurchaseNotification = () => {
    if (purchaseNotificationTimeoutRef.current) {
      clearTimeout(purchaseNotificationTimeoutRef.current);
      purchaseNotificationTimeoutRef.current = null;
    }
    showNextPurchaseNotification();
  };

  useEffect(() => {
    if (!eventHistory?.events) return;

    const visibleNotifications = getVisiblePurchaseNotifications(eventHistory, eventMatchTime);

    visibleNotifications.forEach((notification) => {
      const notificationId = notification.offsetSeconds;
      if (processedPurchaseNotificationsRef.current.has(notificationId)) return;

      processedPurchaseNotificationsRef.current.add(notificationId);
      addPurchaseNotificationToQueue({
        message: notification.message,
        icon: notification.icon,
        showIcon: notification.showIcon,
        offerColors: notification.offerColors,
      });
    });
  }, [eventHistory, eventMatchTime]);

  useEffect(() => {
    if (!currentPurchaseNotification && purchaseNotificationQueue.length > 0) {
      showNextPurchaseNotification();
    }
  }, [purchaseNotificationQueue.length, currentPurchaseNotification]);

  useEffect(() => {
    return () => {
      if (purchaseNotificationTimeoutRef.current) {
        clearTimeout(purchaseNotificationTimeoutRef.current);
      }
    };
  }, []);

  // En YouTube il n'y a pas de MP4 (fetchPublicPlayback n'a rien à renvoyer) : on ne bloque pas dessus.
  if (playbackLoading && !isYouTube) {
    return <SplashScreen label={t('live.loading')} />;
  }

  if (playbackError && !isYouTube) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">{t('live.playbackError')}</p>
          <p className="text-sm text-gray-500">{playbackError}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-svh min-h-0 flex-col overflow-hidden lg:flex-row items-stretch justify-center lg:gap-6 gap-4"
      style={{ backgroundColor: visualIdentity?.backgroundColor }}
    >
      {/* left section — flex-1 + min-h-0 évite le crop iOS (100vh) et libère l’espace pour les contrôles */}
      <div className="w-full flex-2 min-h-0 lg:h-full  flex flex-col justify-between p-4 lg:w-[920px] lg:gap-6 lg:ml-6 lg:py-6">
        {/* Survey */}
        {activeSurvey?.isVisible &&
          publicSurvey &&
          publicSession?.visualIdentity &&
          publicSurvey.id === activeSurvey.resourceId &&
          !hasUserSubmittedSurvey && (
            <div className="lg:hidden block">
              <div className="">
                <SurveyCard
                  survey={publicSurvey}
                  visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                  isAttendee
                  showResults={activeSurvey.showResults}
                  webinarId={webinarId || undefined}
                  workspaceId={publicSession.session.workspaceId}
                  sessionId={publicSession.session.id}
                  authorization={token || undefined}
                  sessionIdentifier={sessionIdentifier}
                />
              </div>
            </div>
          )}

        {/* Quiz */}
        {activeQuiz?.isVisible &&
          publicQuiz &&
          publicSession?.visualIdentity &&
          publicQuiz.id === activeQuiz.resourceId &&
          !hasUserSubmittedQuiz && (
            <div className="lg:hidden block">
              {publicQuiz.enabledDuringLive === false ? (
                <div className="">
                  <QuizCard
                    quiz={publicQuiz}
                    visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                    isAttendee
                    webinarId={webinarId || undefined}
                    sessionId={publicSession.session.id}
                    authorization={token || undefined}
                    sessionIdentifier={sessionIdentifier}
                  />
                </div>
              ) : (
                <div
                  className="absolute inset-0 z-50 flex flex-col justify-between"
                  style={{
                    backgroundColor: visualIdentity?.theme === 'LIGHT' ? '#C5C5CA' : '#6D6E7A',
                  }}
                >
                  <div className="flex-1 flex items-center justify-center px-4">
                    <QuizCard
                      quiz={publicQuiz}
                      visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                      isAttendee
                      webinarId={webinarId || undefined}
                      sessionId={publicSession.session.id}
                      authorization={token || undefined}
                      sessionIdentifier={sessionIdentifier}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Offer */}
        {activeOffer?.isVisible &&
          publicOffer &&
          publicSession?.visualIdentity &&
          publicOffer.id === activeOffer.resourceId && (
            <div className="lg:hidden block">
              <div className="">
                <OfferCard
                  offer={publicOffer}
                  visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                  attendeeView
                  webinarId={webinarId || undefined}
                  sessionIdentifier={sessionIdentifier}
                  authorization={token || undefined}
                />
              </div>
            </div>
          )}

        <div className="w-full flex-1 min-h-0 flex flex-col items-center justify-center lg:gap-6 gap-4">
          {/* Zone vidéo : prend la hauteur restante en desktop pour que le bloc 16:9 s'y ajuste
              (piloté par la hauteur) et laisse la place aux contrôles → plus de scroll ni de coupe. */}
          <div className="w-full flex items-center justify-center min-h-0 lg:flex-1">
            {/* video replay — conteneur aspect-video: sur iOS la vidéo h-fit peut rester à 0 avant les métadonnées */}
            <div
              className={cn(
                'relative aspect-video rounded-[20px] overflow-hidden w-full lg:w-auto lg:h-full',
                '[&_video::-webkit-media-controls]:hidden',
                '[&_video::-webkit-media-controls-enclosure]:hidden',
                activeOffer?.isVisible || activeQuiz?.isVisible || activeSurvey?.isVisible
                  ? 'max-w-[350px] lg:max-w-full'
                  : 'max-w-[450px] md:max-w-[550px] lg:max-w-full',
              )}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {isYouTube ? (
                <YouTubeReplayPlayer
                  youtubeUrl={session?.youtubeUrl}
                  onTimeUpdate={(seconds, { isSeek }) => applyPlaybackTime(seconds, { isSeek })}
                  onEnded={() => {
                    setIsPlaying(false);
                    if (webinarId && sessionIdentifier && token) {
                      const waitingRoomUrl = `/thank-you/${webinarId}/${sessionIdentifier}?token=${token}`;
                      void Promise.resolve(sendReplayHeartbeat()).finally(() => {
                        navigate(waitingRoomUrl);
                      });
                    }
                  }}
                />
              ) : (
                playbackInfo?.mp4Url && (
                  <video
                    ref={videoRef}
                    className="absolute inset-0 size-full rounded-[20px] object-contain origin-top-left scale-[1.005]"
                    src={playbackInfo?.mp4Url}
                    playsInline
                    // Attribut WebKit historique (Safari / anciens iOS) — complète playsInline côté DOM
                    {...{ 'webkit-playsinline': 'true' }}
                    controls={false}
                    disablePictureInPicture
                    preload="metadata"
                    muted={isMuted}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onLoadedMetadata={(e) => {
                      syncDurationFromVideo(e.currentTarget);
                      applyPlaybackTime(e.currentTarget.currentTime, { isSeek: true });
                    }}
                    onSeeked={(e) => {
                      applyPlaybackTime(e.currentTarget.currentTime, { isSeek: true });
                    }}
                    onTimeUpdate={(e) => {
                      syncDurationFromVideo(e.currentTarget);
                      applyPlaybackTime(e.currentTarget.currentTime);
                    }}
                    onEnded={() => {
                      setIsPlaying(false);
                      if (webinarId && sessionIdentifier && token) {
                        const waitingRoomUrl = `/thank-you/${webinarId}/${sessionIdentifier}?token=${token}`;
                        void Promise.resolve(sendReplayHeartbeat()).finally(() => {
                          navigate(waitingRoomUrl);
                        });
                      }
                    }}
                  />
                )
              )}

              {/* Barre de progression - visible uniquement au hover (lecteur natif uniquement) */}
              {!isYouTube && isHovering && (
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div
                    className="w-full h-1 bg-white/30 rounded-full cursor-pointer transition-opacity duration-200"
                    onClick={handleProgressClick}
                    onPointerDown={handleProgressPointerDown}
                    onPointerMove={handleProgressPointerMove}
                    onPointerUp={handleProgressPointerUp}
                    onPointerCancel={handleProgressPointerUp}
                  >
                    <div
                      className="h-full bg-white rounded-full transition-all duration-150"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Reactions */}
              {visibleReactions.map((reaction) => (
                <div
                  key={reaction.id}
                  className="absolute top-1/3 left-10 translate-y-[-50%] pointer-events-none z-50 animate-rise-up"
                  style={{
                    left: `calc(10px + ${reaction.left}px)`,
                  }}
                >
                  <div className="text-5xl">{reaction.text}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Contrôles — z-elevation + safe area: accessibles au-dessus de la vidéo sur iPhone.
              En YouTube on s'appuie sur les contrôles natifs du lecteur. */}
          {!isYouTube && (
            <div className="relative z-20 mt-2 shrink-0 pb-[max(0.5rem,env(safe-area-inset-bottom))] touch-manipulation">
              <Controle
                onPlay={playVideo}
                onPause={pauseVideo}
                onGoBack={goBack}
                onGoForward={goForward}
                onRefresh={refreshVideo}
                onToggleMute={toggleMute}
                // onDownload={downloadVideo}
                isPlaying={isPlaying}
                isMuted={isMuted}
              />
            </div>
          )}
        </div>

        <div className=" items-center justify-between hidden lg:block">
          <FootBarReplay
            currentTime={currentTime}
            visualIdentity={visualIdentity as WorkspaceVisualIdentity}
          />
        </div>
      </div>

      {/* right section */}
      <div className="w-full flex-1 min-h-0 lg:flex-none lg:w-[456px] lg:pr-6 flex flex-col gap-6 lg:h-svh">
        <div
          className={cn('h-full flex flex-col min-h-0 relative')}
          style={{
            backgroundColor:
              visualIdentity?.theme === 'LIGHT'
                ? 'rgba(31, 31, 31, 0.03)'
                : 'rgba(255, 255, 255, 0.1)',
          }}
        >
          {currentPurchaseNotification && visualIdentity && (
            <div className="p-2">
              <PopupBuyerCard
                message={currentPurchaseNotification.message}
                icon={currentPurchaseNotification.icon}
                showIcon={currentPurchaseNotification.showIcon}
                visualIdentity={visualIdentity as any}
                onClose={handleClosePurchaseNotification}
                offerColors={currentPurchaseNotification.offerColors}
              />
            </div>
          )}

          {/* Survey */}
          <div className="lg:block hidden">
            {activeSurvey?.isVisible &&
              publicSurvey &&
              publicSession?.visualIdentity &&
              publicSurvey.id === activeSurvey.resourceId &&
              !hasUserSubmittedSurvey && (
                <div className="p-2">
                  <SurveyCard
                    survey={publicSurvey}
                    visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                    isAttendee
                    showResults={activeSurvey.showResults}
                    webinarId={webinarId || undefined}
                    workspaceId={publicSession.session.workspaceId}
                    sessionId={publicSession.session.id}
                    authorization={token || undefined}
                    sessionIdentifier={sessionIdentifier}
                  />
                </div>
              )}
          </div>

          {/* Quiz */}
          <div className="lg:block hidden">
            {activeQuiz?.isVisible &&
              publicQuiz &&
              publicSession?.visualIdentity &&
              publicQuiz.id === activeQuiz.resourceId &&
              !hasUserSubmittedQuiz && (
                <>
                  {publicQuiz.enabledDuringLive === false ? (
                    <div className="p-2">
                      <QuizCard
                        quiz={publicQuiz}
                        visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                        isAttendee
                        webinarId={webinarId || undefined}
                        sessionId={publicSession.session.id}
                        authorization={token || undefined}
                        sessionIdentifier={sessionIdentifier}
                      />
                    </div>
                  ) : (
                    <div
                      className="absolute inset-0 z-50 flex flex-col justify-between"
                      style={{
                        backgroundColor: visualIdentity?.theme === 'LIGHT' ? '#C5C5CA' : '#6D6E7A',
                      }}
                    >
                      <div className="flex-1 flex items-center justify-center px-4">
                        <QuizCard
                          quiz={publicQuiz}
                          visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                          isAttendee
                          webinarId={webinarId || undefined}
                          sessionId={publicSession.session.id}
                          authorization={token || undefined}
                          sessionIdentifier={sessionIdentifier}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
          </div>

          {/* Offer */}
          <div className="lg:block hidden">
            {activeOffer?.isVisible &&
              publicOffer &&
              publicSession?.visualIdentity &&
              publicOffer.id === activeOffer.resourceId && (
                <div className="p-2">
                  <OfferCard
                    offer={publicOffer}
                    visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                    attendeeView
                    webinarId={webinarId || undefined}
                    sessionIdentifier={sessionIdentifier}
                    authorization={token || undefined}
                  />
                </div>
              )}
          </div>

          {/* Fichiers & Liens */}
          {activeSharedResource?.isVisible && webinarId && sessionIdentifier && token && (
            <div className="p-2">
              <SharedResourceViewer
                webinarId={webinarId}
                sessionIdentifier={sessionIdentifier}
                authorization={token}
                visualIdentity={visualIdentity as WorkspaceVisualIdentity}
                resources={[activeSharedResource.resource]}
              />
            </div>
          )}

          {/* chat */}
          <ChatViewReplay
            eventHistory={eventHistory}
            currentTime={eventMatchTime}
            visualIdentity={visualIdentity as any}
          />
        </div>
      </div>
    </div>
  );
}
